<div class="dashboard-content model-3d-screen" style="padding: 120px 40px 50px">
  <!-- Page Heading -->
  <div class="page-heading">
    <div>
      <h3>
        <i class="fa-solid fa-cube" style="color: #5304fb; margin-right: 8px;"></i>
        {{ 'model3d.title' | translate }}
      </h3>
      <p class="page-subtitle">{{ 'model3d.subtitle' | translate }}</p>
    </div>
  </div>

  <div class="row g-4">
    <!-- UPLOAD CARD -->
    <div class="col-12 col-lg-7">
      <div class="luqta-card h-100">
        <div class="card-heading">
          <h3>{{ 'model3d.upload-model' | translate }}</h3>
          <p class="card-subtitle">
            {{ 'model3d.upload-hint' | translate: { formats: allowedExtensions.join(', '), size: maxSizeMb } }}
          </p>
        </div>

        <input type="file" #modelFileInput [accept]="acceptAttr" (change)="onFileSelected($event)" class="d-none" />

        <!-- Dropzone (no file picked yet) -->
        <div *ngIf="!selectedFile && !isUploading" class="model-dropzone-box" [class.dragover]="isDragOver"
          (click)="triggerFileInput()" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)">
          <div class="dropzone-icon-circle">
            <i class="fa-solid fa-cloud-arrow-up"></i>
          </div>
          <div class="dropzone-title">{{ 'model3d.dropzone-title' | translate }}</div>
          <div class="dropzone-desc">{{ 'model3d.dropzone-desc' | translate }}</div>
          <div class="dropzone-formats">
            <span *ngFor="let ext of allowedExtensions" class="format-chip">.{{ ext }}</span>
          </div>
        </div>

        <!-- Picked file / uploading state -->
        <div *ngIf="selectedFile || isUploading" class="selected-file-card">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div class="file-meta d-flex align-items-center gap-3">
              <div class="file-icon-box">
                <i class="fa-solid fa-cube"></i>
              </div>
              <div>
                <div class="file-name">{{ selectedFile?.name }}</div>
                <div class="file-sub">
                  <span class="ext-tag">{{ getExtension(selectedFile?.name || '') | uppercase }}</span>
                  <span>{{ formatSize(selectedFile?.size || 0) }}</span>
                </div>
              </div>
            </div>

            <div class="d-flex align-items-center gap-2">
              <button *ngIf="!isUploading" type="button" class="btn-modal-cancel" (click)="clearFile()">
                {{ 'model3d.remove' | translate }}
              </button>
              <button *ngIf="!isUploading" type="button" class="btn-modal-submit" (click)="uploadModel()">
                <i class="fa-solid fa-cloud-arrow-up me-2"></i>
                {{ 'model3d.upload' | translate }}
              </button>
              <button *ngIf="isUploading" type="button" class="btn-modal-cancel" (click)="cancelUpload()">
                {{ 'model3d.cancel' | translate }}
              </button>
            </div>
          </div>

          <!-- Model name (sent as the `name` param) -->
          <div class="model-name-field" *ngIf="!isUploading">
            <label class="field-label" for="modelName">{{ 'model3d.model-name' | translate }}</label>
            <input id="modelName" type="text" class="field-input" [(ngModel)]="modelName"
              [placeholder]="'model3d.model-name-placeholder' | translate" maxlength="100" />
            <small class="field-hint">{{ 'model3d.model-name-hint' | translate }}</small>
          </div>

          <!-- Progress -->
          <div *ngIf="isUploading" class="upload-progress-wrapper">
            <div class="progress-track">
              <div class="progress-fill" [style.width.%]="uploadProgress"></div>
            </div>
            <div class="progress-label">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              {{ 'model3d.uploading' | translate }} {{ uploadProgress }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RESULT CARD -->
    <div class="col-12 col-lg-5">
      <div class="luqta-card h-100">
        <div class="card-heading">
          <h3>{{ 'model3d.result' | translate }}</h3>
          <p class="card-subtitle">{{ 'model3d.result-desc' | translate }}</p>
        </div>

        <!-- Empty result -->
        <div *ngIf="!uploadedModel" class="empty-result-box">
          <div class="empty-icon-circle">
            <i class="fa-solid fa-link-slash"></i>
          </div>
          <div class="empty-title">{{ 'model3d.no-result' | translate }}</div>
          <div class="empty-subtitle">{{ 'model3d.no-result-desc' | translate }}</div>
        </div>

        <!-- Uploaded result -->
        <div *ngIf="uploadedModel" class="result-box">
          <div class="result-success-banner">
            <i class="fa-solid fa-circle-check"></i>
            <span>{{ 'model3d.upload-success' | translate }}</span>
          </div>

          <div class="result-row" *ngIf="uploadedModel.name">
            <span class="result-label">{{ 'model3d.model-name' | translate }}</span>
            <span class="result-value">{{ uploadedModel.name }}</span>
          </div>
          <div class="result-row">
            <span class="result-label">{{ 'model3d.file-name' | translate }}</span>
            <span class="result-value">{{ uploadedFile?.name || '—' }}</span>
          </div>
          <div class="result-row">
            <span class="result-label">{{ 'model3d.file-size' | translate }}</span>
            <span class="result-value">{{ formatSize(uploadedFile?.size || 0) }}</span>
          </div>
          <div class="result-row" *ngIf="uploadedFile?.mime">
            <span class="result-label">{{ 'model3d.mime-type' | translate }}</span>
            <span class="result-value">{{ uploadedFile?.mime }}</span>
          </div>

          <div class="result-url-block" *ngIf="uploadedModelUrl">
            <span class="result-label">{{ 'model3d.model-url' | translate }}</span>
            <div class="url-input-group">
              <input type="text" class="url-input" [value]="uploadedModelUrl" readonly />
              <button type="button" class="url-action-btn primary"
                (click)="openPreview(uploadedModelUrl, uploadedFile?.name || uploadedModel.name || '', uploadedFile?.size || 0)"
                [nzTooltipTitle]="'model3d.preview' | translate" nz-tooltip
                [attr.aria-label]="'model3d.preview' | translate">
                <i class="fa-solid fa-eye"></i>
              </button>
              <button type="button" class="url-action-btn" (click)="copyUrl(uploadedModelUrl, $event)"
                [nzTooltipTitle]="'model3d.copy-url' | translate" nz-tooltip
                [attr.aria-label]="'model3d.copy-url' | translate">
                <i class="fa-regular fa-copy"></i>
              </button>
              <a class="url-action-btn" [href]="uploadedModelUrl" target="_blank" rel="noopener noreferrer"
                [nzTooltipTitle]="'model3d.open-url' | translate" nz-tooltip
                [attr.aria-label]="'model3d.open-url' | translate">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </div>
            <small class="url-hint">{{ 'model3d.model-url-hint' | translate }}</small>
          </div>

          <div class="result-missing-url" *ngIf="!uploadedModelUrl">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span>{{ 'model3d.no-url' | translate }}</span>
          </div>

          <button type="button" class="btn-raw-toggle" (click)="toggleRawResponse()">
            <i class="fa-solid" [ngClass]="showRawResponse ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            {{ (showRawResponse ? 'model3d.hide-response' : 'model3d.view-response') | translate }}
          </button>
          <pre *ngIf="showRawResponse" class="raw-response">{{ rawResponseJson }}</pre>
        </div>
      </div>
    </div>
  </div>

  <!-- MY MODELS (GET /my_models) -->
  <div class="luqta-card mt-4">
    <div class="card-heading d-flex justify-content-between align-items-center flex-wrap gap-3">
      <div>
        <h3>{{ 'model3d.my-models' | translate }} <span class="count-badge" *ngIf="models.length">{{ models.length }}</span></h3>
        <p class="card-subtitle">{{ 'model3d.my-models-desc' | translate }}</p>
      </div>
      <button type="button" class="btn-modal-cancel" (click)="loadModels()" [disabled]="isLoadingModels">
        <i class="fa-solid fa-rotate-right me-2" [class.fa-spin]="isLoadingModels"></i>
        {{ 'model3d.refresh' | translate }}
      </button>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoadingModels" class="text-center py-4">
      <div class="spinner-border text-primary" role="status" style="width: 2rem; height: 2rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Empty -->
    <div *ngIf="!isLoadingModels && models.length === 0" class="empty-result-box">
      <div class="empty-icon-circle">
        <i class="fa-solid fa-cube"></i>
      </div>
      <div class="empty-title">{{ 'model3d.no-models' | translate }}</div>
      <div class="empty-subtitle">{{ 'model3d.no-models-desc' | translate }}</div>
    </div>

    <!-- List -->
    <div class="history-list" *ngIf="!isLoadingModels && models.length > 0">
      <div class="history-item" *ngFor="let row of models; trackBy: trackByRow">
        <div class="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
          <div class="file-icon-box small">
            <i class="fa-solid fa-cube"></i>
          </div>
          <div class="min-w-0">
            <div class="file-name">{{ row.title }}</div>
            <div class="file-sub">
              <span class="ext-tag" *ngIf="row.fileName">{{ getExtension(row.fileName) | uppercase }}</span>
              <span *ngIf="row.fileName">{{ row.fileName }}</span>
              <span>{{ formatSize(row.size) }}</span>
              <span class="text-muted" *ngIf="row.createdAt">{{ row.createdAt | date: 'medium' }}</span>
            </div>
            <div class="history-url" *ngIf="row.url">{{ row.url }}</div>
            <div class="history-url is-missing" *ngIf="!row.url">
              <i class="fa-solid fa-triangle-exclamation me-1"></i>{{ 'model3d.no-url' | translate }}
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center gap-2">
          <button type="button" class="url-action-btn primary" [disabled]="!row.url"
            (click)="openPreview(row.url, row.title, row.size)"
            [nzTooltipTitle]="(row.url ? 'model3d.preview' : 'model3d.no-url') | translate" nz-tooltip
            [attr.aria-label]="'model3d.preview' | translate">
            <i class="fa-solid fa-eye"></i>
          </button>
          <button type="button" class="url-action-btn" [disabled]="!row.url" (click)="copyUrl(row.url, $event)"
            [nzTooltipTitle]="'model3d.copy-url' | translate" nz-tooltip
            [attr.aria-label]="'model3d.copy-url' | translate">
            <i class="fa-regular fa-copy"></i>
          </button>
          <button type="button" class="url-action-btn danger" [disabled]="!row.id || deletingId === row.id"
            (click)="confirmDelete(row)" [nzTooltipTitle]="'model3d.delete' | translate" nz-tooltip
            [attr.aria-label]="'model3d.delete' | translate">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 3D preview popup (shared with the contest builder) -->
  <app-model-preview [(visible)]="isPreviewOpen" [url]="previewSourceUrl" [title]="previewTitle"
    [size]="previewSize"></app-model-preview>
</div>
